import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api, { formatApiErrorDetail } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Card, CardContent } from "../components/ui/card";
import { Loader2, CheckCircle2 } from "lucide-react";

export default function RegisterPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(null);
  const [form, setForm] = useState({
    company_name: "", gstin: "", contact_person: "", mobile: "", address: "",
    name: "", email: "", password: "", confirm: "",
  });

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (form.password !== form.confirm) return setError("Passwords do not match");
    if (form.password.length < 6) return setError("Password must be at least 6 characters");
    setLoading(true);
    try {
      const { data } = await api.post("/auth/register", {
        name: form.name, email: form.email, password: form.password,
        company_name: form.company_name, gstin: form.gstin,
        contact_person: form.contact_person || form.name,
        mobile: form.mobile, address: form.address,
      });
      setSuccess(data);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 p-6">
        <Card className="max-w-md w-full" data-testid="register-success-card">
          <CardContent className="p-8 text-center">
            <div className="w-14 h-14 rounded-full bg-green-100 text-green-700 flex items-center justify-center mx-auto mb-4">
              <CheckCircle2 className="w-7 h-7" />
            </div>
            <h2 className="heading text-2xl font-bold text-slate-900">Registration submitted!</h2>
            <p className="text-slate-600 mt-2">
              Your account is pending admin approval. You&apos;ll be able to sign in once an administrator approves your request.
            </p>
            <div className="mt-4 inline-block bg-orange-50 text-orange-700 border border-orange-200 rounded px-3 py-1.5 text-sm">
              Your customer code: <span className="font-mono font-semibold">{success.customer_code}</span>
            </div>
            <div className="mt-6">
              <Button onClick={() => navigate("/login")} className="bg-orange-600 hover:bg-orange-700" data-testid="back-to-login-button">
                Back to Login
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 py-10 px-4">
      <div className="max-w-3xl mx-auto">
        <div className="flex items-center gap-2 mb-6">
          <div className="w-10 h-10 rounded-md brand-gradient flex items-center justify-center text-white font-bold heading">GR</div>
          <div>
            <div className="heading font-bold text-slate-900">Golden Rolls</div>
            <div className="text-xs text-slate-500">Return Management</div>
          </div>
        </div>

        <Card>
          <CardContent className="p-8">
            <h1 className="heading text-3xl font-bold text-slate-900">Create your account</h1>
            <p className="text-slate-500 mt-1">Register as a customer to raise & track return requests. Your account will be activated by our admin team.</p>

            <form onSubmit={onSubmit} className="mt-6 space-y-6">
              <section>
                <div className="text-sm font-semibold text-slate-900 mb-3 heading">Company Details</div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="md:col-span-2">
                    <Label>Company Name *</Label>
                    <Input required value={form.company_name} onChange={(e) => set("company_name", e.target.value)} data-testid="reg-company-name" />
                  </div>
                  <div>
                    <Label>GSTIN</Label>
                    <Input value={form.gstin} onChange={(e) => set("gstin", e.target.value)} placeholder="27ABCDE1234F1Z5" data-testid="reg-gstin" />
                  </div>
                  <div>
                    <Label>Mobile</Label>
                    <Input value={form.mobile} onChange={(e) => set("mobile", e.target.value)} placeholder="+91 98765 43210" data-testid="reg-mobile" />
                  </div>
                  <div className="md:col-span-2">
                    <Label>Address</Label>
                    <Textarea rows={2} value={form.address} onChange={(e) => set("address", e.target.value)} data-testid="reg-address" />
                  </div>
                </div>
              </section>

              <section>
                <div className="text-sm font-semibold text-slate-900 mb-3 heading">Contact Person & Login</div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label>Your Name *</Label>
                    <Input required value={form.name} onChange={(e) => set("name", e.target.value)} data-testid="reg-name" />
                  </div>
                  <div>
                    <Label>Contact Person</Label>
                    <Input value={form.contact_person} onChange={(e) => set("contact_person", e.target.value)} placeholder="(defaults to your name)" data-testid="reg-contact" />
                  </div>
                  <div>
                    <Label>Email *</Label>
                    <Input type="email" required value={form.email} onChange={(e) => set("email", e.target.value)} data-testid="reg-email" />
                  </div>
                  <div></div>
                  <div>
                    <Label>Password *</Label>
                    <Input type="password" required value={form.password} onChange={(e) => set("password", e.target.value)} data-testid="reg-password" />
                  </div>
                  <div>
                    <Label>Confirm Password *</Label>
                    <Input type="password" required value={form.confirm} onChange={(e) => set("confirm", e.target.value)} data-testid="reg-confirm" />
                  </div>
                </div>
              </section>

              {error && (
                <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded px-3 py-2" data-testid="register-error">
                  {error}
                </div>
              )}

              <div className="flex items-center justify-between pt-2">
                <Link to="/login" className="text-sm text-slate-600 hover:text-orange-700">
                  Already have an account? <span className="font-medium">Sign in</span>
                </Link>
                <Button type="submit" disabled={loading} className="bg-orange-600 hover:bg-orange-700" data-testid="register-submit-button">
                  {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                  Create Account
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
